import React from 'react';
import EngineeringSlideshowBackground from '../components/EngineeringSlideshowBackground';
import { motion } from 'framer-motion';
import { Code, Cpu, Zap, Wrench, Building, Plane, Car, Microscope, ChevronRight, Search as SearchIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

const EngineeringPage: React.FC = () => {
  const branches = [
    { name: 'Computer Science', icon: <Code className="h-5 w-5" /> },
    { name: 'Electronics', icon: <Cpu className="h-5 w-5" /> },
    { name: 'Electrical', icon: <Zap className="h-5 w-5" /> },
    { name: 'Mechanical', icon: <Wrench className="h-5 w-5" /> },
    { name: 'Civil', icon: <Building className="h-5 w-5" /> },
    { name: 'Aerospace', icon: <Plane className="h-5 w-5" /> },
    { name: 'Automobile', icon: <Car className="h-5 w-5" /> },
    { name: 'Biotechnology', icon: <Microscope className="h-5 w-5" /> }
  ];

  const examInfo = [
    {
      name: "JEE Main",
      description: "For admission to NITs, IIITs, and GFTIs",
      frequency: "Twice a year",
      difficulty: "Moderate"
    },
    {
      name: "JEE Advanced",
      description: "For admission to IITs",
      frequency: "Once a year",
      difficulty: "Very High"
    },
    {
      name: "BITSAT",
      description: "For admission to BITS campuses",
      frequency: "Multiple sessions",
      difficulty: "High"
    },
    {
      name: "State CETs",
      description: "For state engineering colleges",
      frequency: "Once a year",
      difficulty: "Moderate"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Engineering Slideshow */}
      <div className="mb-8">
        <EngineeringSlideshowBackground />
      </div>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700 text-white py-20 relative overflow-hidden">
        {/* Modern Background Pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="w-full h-full bg-gradient-to-br from-white/5 to-transparent"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="inline-block p-2 bg-white/10 backdrop-blur-sm rounded-2xl mb-6">
              <span className="text-blue-200 text-sm font-medium px-4 py-1 bg-white/10 rounded-xl">
                🚀 Engineering Excellence
              </span>
            </div>
            <h1 className="text-5xl md:text-7xl font-black mb-6 bg-gradient-to-r from-white via-blue-100 to-purple-200 bg-clip-text text-transparent">
              Engineering Future
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
              Discover cutting-edge engineering programs and world-class institutions that shape tomorrow's innovators
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="/colleges"
                  className="inline-block px-8 py-4 bg-white text-blue-600 font-bold rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300"
                >
                  Explore Programs
                </Link>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="/premium-services"
                  className="inline-block px-8 py-4 border-2 border-white/30 backdrop-blur-sm text-white font-bold rounded-2xl hover:bg-white/10 transition-all duration-300"
                >
                  Get Guidance
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TNEA Admission Status Banner */}
      <div className="w-full bg-gradient-to-r from-red-600 via-orange-500 to-red-600 text-white py-4 overflow-hidden relative z-10 shadow-lg">
        <div className="absolute inset-0 bg-black/20"></div>
        <motion.div 
          className="whitespace-nowrap relative z-20 flex items-center"
          initial={{ x: "100vw" }}
          animate={{ x: "-100%" }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear",
            repeatType: "loop"
          }}
        >
          <div className="flex items-center space-x-8 text-xl font-bold tracking-wide">
            <span>🚨 IMPORTANT: TNEA 2025 Admissions were closed on 15/09/2025</span>
            <span>•</span>
            <span>📞 For guidance on alternative admissions and next steps, contact our experts ,Contact: +91 77086 77132 (Enquiry) | +91 93610 27132 (Advisor)</span>
            <span>•</span>
            <span>🎓 We help you find the best alternative pathways</span>
            <span>•</span>
          </div>
        </motion.div>
      </div>

      {/* College Types Section */}
      <section className="py-16 bg-white relative">
        {/* Modern Decorative Elements */}
        <div className="absolute top-0 left-0 w-32 h-32 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full blur-3xl opacity-30"></div>
        <div className="absolute bottom-0 right-0 w-40 h-40 bg-gradient-to-br from-indigo-100 to-pink-100 rounded-full blur-3xl opacity-30"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-block p-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl mb-6">
              <div className="bg-white px-6 py-2 rounded-xl">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 font-bold">
                  College Categories
                </span>
              </div>
            </div>
            <h2 className="text-4xl md:text-5xl font-black mb-6 text-slate-800">
              Choose Your Path
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Select the perfect engineering institution type that aligns with your goals and aspirations
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Government Colleges */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="group relative overflow-hidden bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100 p-8 rounded-3xl border border-blue-200/50 hover:shadow-2xl transition-all duration-500"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-indigo-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              <div className="relative">
                <div className="flex items-center mb-6">
                  <div className="p-4 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-lg">
                    <Building className="h-8 w-8 text-white" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-2xl font-black text-slate-800">Government Colleges</h3>
                    <p className="text-blue-600 font-semibold">Premium • Affordable</p>
                  </div>
                </div>
                <p className="text-slate-600 mb-6 text-lg leading-relaxed">
                  Government-funded institutions offering world-class education with exceptional affordability and proven excellence
                </p>
                <div className="grid grid-cols-1 gap-3">
                  <div className="flex items-center p-3 bg-white/70 backdrop-blur-sm rounded-xl">
                    <div className="w-3 h-3 bg-gradient-to-r from-emerald-400 to-green-500 rounded-full mr-3"></div>
                    <span className="font-medium text-slate-700">Ultra-low fees structure</span>
                  </div>
                  <div className="flex items-center p-3 bg-white/70 backdrop-blur-sm rounded-xl">
                    <div className="w-3 h-3 bg-gradient-to-r from-emerald-400 to-green-500 rounded-full mr-3"></div>
                    <span className="font-medium text-slate-700">Top-tier placement records</span>
                  </div>
                  <div className="flex items-center p-3 bg-white/70 backdrop-blur-sm rounded-xl">
                    <div className="w-3 h-3 bg-gradient-to-r from-emerald-400 to-green-500 rounded-full mr-3"></div>
                    <span className="font-medium text-slate-700">IITs, NITs, GFTIs network</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Self-Financing Colleges */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="group relative overflow-hidden bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100 p-8 rounded-3xl border border-purple-200/50 hover:shadow-2xl transition-all duration-500"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              <div className="relative">
                <div className="flex items-center mb-6">
                  <div className="p-4 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl shadow-lg">
                    <Wrench className="h-8 w-8 text-white" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-2xl font-black text-slate-800">Self-Financing Colleges</h3>
                    <p className="text-purple-600 font-semibold">Flexible • Modern</p>
                  </div>
                </div>
                <p className="text-slate-600 mb-8 text-lg leading-relaxed">
                  Private institutions with innovative curricula, cutting-edge infrastructure, and industry-aligned programs
                </p>
                
                {/* Autonomous Sub-category */}
                <div className="mb-6 p-6 bg-white/80 backdrop-blur-sm rounded-2xl border border-purple-100/50 shadow-lg">
                  <h4 className="text-lg font-bold text-purple-700 mb-4 flex items-center">
                    <div className="w-2 h-2 bg-gradient-to-r from-purple-400 to-purple-600 rounded-full mr-3"></div>
                    Autonomous Colleges
                  </h4>
                  <div className="grid grid-cols-1 gap-2">
                    <div className="flex items-center p-2 bg-purple-50/50 rounded-xl">
                      <div className="w-2 h-2 bg-gradient-to-r from-purple-400 to-purple-600 rounded-full mr-3"></div>
                      <span className="text-sm font-medium text-slate-700">Independent curriculum design</span>
                    </div>
                    <div className="flex items-center p-2 bg-purple-50/50 rounded-xl">
                      <div className="w-2 h-2 bg-gradient-to-r from-purple-400 to-purple-600 rounded-full mr-3"></div>
                      <span className="text-sm font-medium text-slate-700">Flexible examination system</span>
                    </div>
                    <div className="flex items-center p-2 bg-purple-50/50 rounded-xl">
                      <div className="w-2 h-2 bg-gradient-to-r from-purple-400 to-purple-600 rounded-full mr-3"></div>
                      <span className="text-sm font-medium text-slate-700">Industry-oriented programs</span>
                    </div>
                  </div>
                </div>

                {/* Non-Autonomous Sub-category */}
                <div className="p-6 bg-white/80 backdrop-blur-sm rounded-2xl border border-pink-100/50 shadow-lg">
                  <h4 className="text-lg font-bold text-pink-700 mb-4 flex items-center">
                    <div className="w-2 h-2 bg-gradient-to-r from-pink-400 to-pink-600 rounded-full mr-3"></div>
                    Non-Autonomous Colleges
                  </h4>
                  <div className="grid grid-cols-1 gap-2">
                    <div className="flex items-center p-2 bg-pink-50/50 rounded-xl">
                      <div className="w-2 h-2 bg-gradient-to-r from-pink-400 to-pink-600 rounded-full mr-3"></div>
                      <span className="text-sm font-medium text-slate-700">University-affiliated curriculum</span>
                    </div>
                    <div className="flex items-center p-2 bg-pink-50/50 rounded-xl">
                      <div className="w-2 h-2 bg-gradient-to-r from-pink-400 to-pink-600 rounded-full mr-3"></div>
                      <span className="text-sm font-medium text-slate-700">Standardized examination pattern</span>
                    </div>
                    <div className="flex items-center p-2 bg-pink-50/50 rounded-xl">
                      <div className="w-2 h-2 bg-gradient-to-r from-pink-400 to-pink-600 rounded-full mr-3"></div>
                      <span className="text-sm font-medium text-slate-700">Traditional academic approach</span>
                    </div>
                    <div className="flex items-center p-2 bg-pink-50/50 rounded-xl">
                      <div className="w-2 h-2 bg-gradient-to-r from-pink-400 to-pink-600 rounded-full mr-3"></div>
                      <span className="text-sm font-medium text-slate-700">Budget-friendly options</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Engineering Branches Overview */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 relative overflow-hidden">
        {/* Modern Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-indigo-500/5 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-block p-1 bg-gradient-to-r from-blue-400 to-purple-400 rounded-2xl mb-6">
              <div className="bg-slate-900 px-6 py-2 rounded-xl">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400 font-bold">
                  Engineering Disciplines
                </span>
              </div>
            </div>
            <h2 className="text-4xl md:text-6xl font-black mb-6 text-white">
              Future-Ready Branches
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
              Explore cutting-edge engineering fields that are shaping the future of technology and innovation
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {branches.map((branch, index) => (
              <motion.div
                key={branch.name}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group relative overflow-hidden bg-white/10 backdrop-blur-lg border border-white/20 p-6 rounded-3xl hover:bg-white/20 transition-all duration-500 hover:scale-105"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <div className="relative">
                  <div className="p-4 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl shadow-2xl mb-6 group-hover:shadow-blue-500/25 transition-all duration-500">
                    {branch.icon}
                  </div>
                  <h3 className="text-xl font-black mb-4 text-white group-hover:text-blue-200 transition-colors duration-300">
                    {branch.name}
                  </h3>
                  <p className="text-slate-300 text-sm leading-relaxed group-hover:text-slate-200 transition-colors duration-300">
                    {branch.name === 'Computer Science' && "AI, Machine Learning, Blockchain, and next-gen software solutions"}
                    {branch.name === 'Electronics' && "IoT, Embedded Systems, 5G Technology, and smart device innovation"}
                    {branch.name === 'Electrical' && "Smart Grids, Renewable Energy, Electric Vehicles, and power innovation"}
                    {branch.name === 'Mechanical' && "Robotics, Automation, 3D Printing, and advanced manufacturing"}
                    {branch.name === 'Civil' && "Smart Cities, Sustainable Construction, and infrastructure innovation"}
                    {branch.name === 'Aerospace' && "Space Technology, Drone Systems, and aviation advancement"}
                    {branch.name === 'Automobile' && "Electric Vehicles, Autonomous Driving, and mobility solutions"}
                    {branch.name === 'Biotechnology' && "Genetic Engineering, Biomedical Devices, and life science innovation"}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Colleges Database Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4 text-gray-900">
              Engineering Colleges Database
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Access comprehensive information about engineering colleges across India
            </p>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link
                to="/colleges"
                className="inline-flex items-center bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
              <SearchIcon className="mr-3 h-6 w-6" />
                Browse All Colleges
                <ChevronRight className="ml-3 h-6 w-6" />
              </Link>
            </motion.div>
            <p className="text-sm text-gray-500 mt-4">
              Search, filter, and compare 160+ engineering colleges with detailed information
            </p>
          </motion.div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="text-center p-6 rounded-lg bg-blue-50 border border-blue-200"
            >
              <div className="bg-blue-600 text-white p-3 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <SearchIcon className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-gray-900">Advanced Search</h3>
              <p className="text-gray-600">Filter by branch, type, location, and fees to find your perfect college</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-center p-6 rounded-lg bg-purple-50 border border-purple-200"
            >
              <div className="bg-purple-600 text-white p-3 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Building className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-gray-900">College Codes</h3>
              <p className="text-gray-600">Official TNEA codes for easy identification and application</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="text-center p-6 rounded-lg bg-green-50 border border-green-200"
            >
              <div className="bg-green-600 text-white p-3 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <ChevronRight className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-gray-900">Detailed Info</h3>
              <p className="text-gray-600">Fees, placements, location, and admission details for each college</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Entrance Exams */}
      <section className="py-16 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4 text-gray-900">
              Engineering Entrance Exams
            </h2>
            <p className="text-lg text-gray-600">
              Key examinations for engineering admissions in India
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {examInfo.map((exam, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-lg shadow-md text-center"
              >
                <h3 className="text-lg font-bold mb-3 text-blue-600">{exam.name}</h3>
                <p className="text-gray-600 text-sm mb-3">{exam.description}</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Frequency:</span>
                    <span className="font-medium">{exam.frequency}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Difficulty:</span>
                    <span className={`font-medium ${
                      exam.difficulty === 'Very High' ? 'text-red-600' :
                      exam.difficulty === 'High' ? 'text-orange-600' : 'text-green-600'
                    }`}>
                      {exam.difficulty}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default EngineeringPage;
